import { jsx } from "react/jsx-runtime";
import { Box } from "braid-design-system";
import { useReducer, useRef, useEffect, memo } from "react";
import { useIsomorphicLayoutEffect } from "../../ApacCandidateHeader.mjs";
import "../../../../hooks/useCountries/.vocab/index.mjs";
import "../../../ApacCandidateFooter/components/Accordion/Accordion.mjs";
import { root, active } from "./AnimatedWidth.css.mjs";
const initialState = { status: "idle" };
const reducer = (state, action) => {
  switch (action.type) {
    case "start": {
      return {
        status: "starting",
        targetDimension: action.targetDimension
      };
    }
    case "started": {
      if (state.status === "animating") {
        return state;
      }
      return {
        status: "animating",
        targetDimension: action.targetDimension
      };
    }
    case "done": {
      return { status: "idle" };
    }
    default: {
      return state;
    }
  }
};
const ANIMATION_TIME_IN_MS = 400;
const useAnimatedWrapper = ({
  changeKeys,
  changeDependsOn,
  changeDimension
}) => {
  var _a;
  const [{ status, targetDimension }, dispatch] = useReducer(
    reducer,
    initialState
  );
  const parentRef = useRef(null);
  const childRef = useRef(null);
  const cleanupTimeoutRef = useRef(null);
  const previousChildDimension = (_a = childRef.current) == null ? void 0 : _a.getBoundingClientRect()[changeDimension];
  const cleanupAfterTransition = () => {
    cleanupTimeoutRef.current = null;
    dispatch({ type: "done" });
  };
  useIsomorphicLayoutEffect(() => {
    const parentElement = parentRef.current;
    const childElement = childRef.current;
    const newTargetElement = changeDependsOn === "parent" ? parentElement : childElement;
    const newTargetDimensionCurrent = newTargetElement == null ? void 0 : newTargetElement.getBoundingClientRect()[changeDimension];
    if (!parentElement || !childElement || !newTargetElement || typeof previousChildDimension !== "number") {
      return;
    }
    if (status === "animating") {
      if (targetDimension === childElement.getBoundingClientRect()[changeDimension]) {
        return;
      }
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      requestAnimationFrame(() => {
        const newTargetDimension = newTargetElement.getBoundingClientRect()[changeDimension];
        dispatch({
          type: "started",
          targetDimension: newTargetDimension
        });
      });
    }
    if (status === "idle") {
      if (previousChildDimension === childElement.getBoundingClientRect()[changeDimension]) {
        return;
      }
      dispatch({ type: "start", targetDimension: previousChildDimension });
      requestAnimationFrame(() => {
        const newTargetDimension = newTargetElement.getBoundingClientRect()[changeDimension];
        dispatch({
          type: "started",
          targetDimension: newTargetDimension === previousChildDimension ? newTargetDimensionCurrent || newTargetDimension : newTargetDimension
        });
      });
    }
  }, changeKeys);
  useEffect(() => {
    if (status === "animating") {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
      cleanupTimeoutRef.current = setTimeout(
        cleanupAfterTransition,
        ANIMATION_TIME_IN_MS + 100
      );
    }
  }, [status]);
  const style = status !== "idle" ? `${targetDimension}px` : "auto";
  return { style, parentRef, childRef, status };
};
const AnimatedWidth = ({
  children,
  changeKeys,
  changeDependsOn = "child"
}) => {
  const { style, parentRef, childRef, status } = useAnimatedWrapper({
    changeKeys,
    changeDependsOn,
    changeDimension: "width"
  });
  const commonProps = {
    className: [
      root,
      {
        [active]: status !== "idle"
      }
    ],
    style: { width: style }
  };
  return /* @__PURE__ */ jsx(
    Box,
    {
      ref: parentRef,
      ...changeDependsOn === "child" ? commonProps : void 0,
      children: /* @__PURE__ */ jsx(
        Box,
        {
          ref: childRef,
          ...changeDependsOn === "parent" ? commonProps : void 0,
          children
        }
      )
    }
  );
};
const AnimatedWidth$1 = memo(AnimatedWidth);
export {
  AnimatedWidth$1 as AnimatedWidth
};
